import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Button, Typography } from '@mui/material'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'
import { changeLocation } from 'modules/User/actions'
import AddressSearch from 'modules/Business/modules/Offers/components/OfferAudience/components/AddressSearch'
import api from 'utils/api'
import { setUseBrowserLocation } from 'utils/location'

const LocationForm = ({ location, onClose }) => {
  const [showSearch, setShowSearch] = useState(false)
  const dispatch = useDispatch()
  const user = useSelector(state => state.user.details)

  /**
   * Silent IP-based fallback (IPStack)
   */
  const fetchFallbackLocation = async () => {
    try {
      console.info('Fetching location from IP Stack...')
      const fullLocation = await api.post('/geo', {})

      if (
        !fullLocation ||
        (!fullLocation.latitude && !fullLocation.longitude && !fullLocation.city)
      ) {
        console.error('Invalid IP Stack response:', fullLocation)
        return
      }

      dispatch(changeLocation(fullLocation))
    } catch (error) {
      console.error('IP Stack API error:', error)
    }
  }

  const handleClick = () => {
    if (onClose) {
      onClose()
    }

    // Save preference to use browser location by default
    setUseBrowserLocation(true)

    if (!navigator.geolocation) {
      fetchFallbackLocation()
      return
    }

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const fullLocation = await api.post('/geo', {
            latitude: coords.latitude,
            longitude: coords.longitude,
          })

          if (
            fullLocation &&
            (fullLocation.latitude || fullLocation.longitude || fullLocation.city)
          ) {
            dispatch(changeLocation(fullLocation, { track: true }))
          }
        } catch (error) {
          console.error('Failed to send precise location:', error)
          fetchFallbackLocation()
        }
      },
      (error) => {
        const PERMISSION_DENIED = 1

        // 🔕 Silent fallback (no messages)
        if (error?.code === PERMISSION_DENIED) {
          fetchFallbackLocation()
        } else {
          fetchFallbackLocation()
        }
      },
      { enableHighAccuracy: false, timeout: 15_000 }
    )
  }

  /**
   * Manual location selection
   */
  const handleSelect = async (locationObject) => {
    onClose?.()
    try {
      const fullLocation = await api.post('/geo', locationObject)
      dispatch(changeLocation(fullLocation, { track: true }))
    } catch (error) {
      console.error('Manual location selection failed:', error)
    }
  }

  return (
    <Box px={2} py={1}>
      <Typography variant="body2" color="textSecondary">
        Filter offers by location:
      </Typography>

      {location && (
        <Box display="flex" alignItems="center" mt={1.5} mb={1}>
          <RoomOutlinedIcon style={{ marginRight: 8 }} />
          <Typography variant="body2">
            {location.city ? `${location.city}, ` : ''}
            {location.region_name
              ? `${location.region_name}, ${location.country_code}`
              : location.country_name}
          </Typography>
        </Box>
      )}

      <Button onClick={handleClick} color="primary">
        Use my current position
      </Button>

      {user && (
        <Button onClick={() => setShowSearch(true)} color="primary">
          Enter location manually
        </Button>
      )}

      {showSearch && (
        <Box p={1}>
          <AddressSearch onSelect={handleSelect} label="Your address" />
        </Box>
      )}
    </Box>
  )
}

export default LocationForm
